import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline, createTheme } from "@mui/material";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import CustomersPage from "./pages/CustomersPage";
import CustomerPage from "./pages/CustomerPage";
import CreateCustomerPage from "./pages/CustomerCreatePage";
import DeviceListPage from "./pages/DeviceListPage";
import DeviceControlsPage from "./pages/DeviceControlsPage";
import DevicePage from "./pages/DevicePage";
import DeviceCleaningConfigurationPage from "./pages/DeviceCleaningConfigurationPage";
import DeviceConnectionStatusPage from "./pages/DeviceConnectionStatusPage";
import PerformanceDashboardPage from "./pages/PerformanceDashboardPage";
import GuidesPage from "./pages/GuidesPage";
import LogsPage from "./pages/LogsPage";
import ServiceDashboard from "./pages/ServiceDashboardPage";
import ServicePage from "./pages/ServicePage";
import ServicesPage from "./pages/ServicesPage";
import ProtectedRoute from "./routes/ProtectedRoute"; // Changed from PrivateRoute
import PublicRoute from "./routes/PublicRoute";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import TestTelemetryPage from "./pages/TestTelemetryPage";
import UserManagementPage from "./pages/UserManagementPage";
import { SSEProvider } from "./context/SSEContext";
import { useAuth } from "./context/AuthContext";
import useFullscreen from "./hooks/useFullscreen";
import ServiceDashboardPage from "./pages/ServiceDashboardPage";
import ServiceCustomerDashboardPage from "./pages/ServiceCustomerDashboardPage";
import DriftsdashboardPage from "./pages/DriftsdashboardPage";
import ConfigureRoutesPage from "./pages/ConfigureRoutesPage";
import ConfigureRouteTemplatePage from "./pages/ConfigureRouteTemplatePage";
import ConfigureServicePlansPage from "./pages/ConfigureServicePlansPage";
import ConfigureServicePlanTemplatePage from "./pages/ConfigureServicePlanTemplatePage";
import ConfigureServiceTasksPage from "./pages/ConfigureServiceTasks";
import ConfigureDevicePlansPage from "./pages/ConfigureDevicePlansPage";
import ConfigureDevicePlanPage from "./pages/ConfigureDevicePlanPage";

// Floating job panels use zIndex values up to ~1700, so raise MUI's default
// tooltip/popover/modal z-indexes (1500) to render above them.
const theme = createTheme({
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1900,
    snackbar: 1950,
    tooltip: 2000,
  },
});

const getLandingPathForRole = (role) => {
  const normalizedRole = String(role || "").toLowerCase();

  switch (normalizedRole) {
    case "control":
      return "/device-controls";
    case "technician":
      return "/my-services";
    case "customer":
      return "/driftsdashboard";
    case "admin":
    case "user":
    default:
      return "/";
  }
};

function LandingRedirect() {
  const { user } = useAuth();
  return <Navigate to={getLandingPathForRole(user?.role)} replace />;
}

// Component to handle fullscreen based on route
function FullscreenHandler() {
  const location = useLocation();
  const { enterFullscreen, exitFullscreen } = useFullscreen(false); // Don't auto-enter

  useEffect(() => {
    // Enable fullscreen for device controls (tablet interface)
    if (location.pathname.startsWith("/device-controls")) {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        enterFullscreen();
      }, 1000);
    } else {
      // Exit fullscreen for other pages (optional)
      // exitFullscreen();
    }
  }, [location, enterFullscreen, exitFullscreen]);

  return null; // This component doesn't render anything
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <SSEProvider>
        <CssBaseline />
        <Router>
          <FullscreenHandler />
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />

            {/* Legacy unauthorized route redirects to the user’s configured landing page */}
            <Route path="/unauthorized" element={<LandingRedirect />} />

            {/* Dashboard - All authenticated users bar Control */}
            <Route
              path="/"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            {/* Device Controls - Control role only */}
            <Route
              path="/device-controls"
              element={
                <ProtectedRoute allowedRoles={["control", "admin"]}>
                  <DeviceListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/device-controls/:rcDeviceId"
              element={
                <ProtectedRoute allowedRoles={["control", "admin"]}>
                  <DeviceControlsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/guides"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "admin",
                    "user",
                    "customer",
                    "technician",
                    "control",
                  ]}
                >
                  <GuidesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/guides/:guideId"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "admin",
                    "user",
                    "customer",
                    "technician",
                    "control",
                  ]}
                >
                  <GuidesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/device/:deviceId"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "user", "customer", "technician"]}
                >
                  <DevicePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/device/:deviceId/controls-setup"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <DeviceCleaningConfigurationPage />
                </ProtectedRoute>
              }
            />

            {/* Jobs - Admin, User, Customer, Technician */}
            <Route
              path="/my-services"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "user", "customer", "technician"]}
                >
                  <ServicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-services/:jobId"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "user", "customer", "technician"]}
                >
                  <ServicePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/service-dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <ServiceDashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/service-dashboard/:customerId"
              element={
                <ProtectedRoute allowedRoles={["admin", "user", "customer"]}>
                  <ServiceCustomerDashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driftsdashboard"
              element={
                <ProtectedRoute allowedRoles={["admin", "user", "customer"]}>
                  <DriftsdashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/configure-routes"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureRoutesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configure-routes/:templateId"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureRouteTemplatePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/configure-service-plans"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureServicePlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configure-service-plans/:templateId"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureServicePlanTemplatePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/configure-service-tasks"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureServiceTasksPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/configure-plan-allocation"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureDevicePlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configure-plan-allocation/:targetType/:targetId"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ConfigureDevicePlanPage />
                </ProtectedRoute>
              }
            />

            {/* Logs - Admin and User only */}
            <Route
              path="/logs"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <LogsPage />
                </ProtectedRoute>
              }
            />

            {/* Customers - Admin and User only */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/:customerId"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <CustomerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-customer"
              element={
                <ProtectedRoute allowedRoles={["admin", "user"]}>
                  <CreateCustomerPage />
                </ProtectedRoute>
              }
            />

            {/* User Management - Admin only */}
            <Route
              path="/user-management"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/register-user"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <RegisterPage />
                </ProtectedRoute>
              }
            />

            {/* Testing/Telemetry - Admin only */}
            <Route
              path="/telemetry"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <TestTelemetryPage />
                </ProtectedRoute>
              }
            />

            {/* Device Connection Status - Admin only */}
            <Route
              path="/device-connection-status"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <DeviceConnectionStatusPage />
                </ProtectedRoute>
              }
            />

            {/* Performance Dashboard - Admin, User, Customer */}
            <Route
              path="/performance-dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin", "user", "customer"]}>
                  <PerformanceDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Account Management - All authenticated users */}
            <Route
              path="/edit-account"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "user", "customer", "technician"]}
                >
                  <AccountPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </SSEProvider>
    </ThemeProvider>
  );
}

export default App;
